import { Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable, of, ReplaySubject } from "rxjs";
import { AngularFireAuth } from "@angular/fire/auth";
import { User, UserData } from "../data/users";
import { environment } from "../../../environments/environment";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";
import { HttpClient } from "@angular/common/http";
import * as firebase from "firebase/app";
import "firebase/firestore";
import { AngularFireFunctions } from "@angular/fire/functions";

@Injectable()
export class UserService {
  baseUrl = environment.apiUrl;
  schoolId = environment.schoolId;
  private dbPath = "/users";
  usersRef: AngularFirestoreCollection<UserData>;

  currentUserSource = new ReplaySubject<User>(1);
  currentUser$: Observable<any> = this.currentUserSource.asObservable();

  // Save logged in user data
  userData: any;
  // Source data
  private _isLogin = new BehaviorSubject(false);
  // Observable data
  $isAuthenticated: Observable<boolean> = this._isLogin.asObservable();

  constructor(
    private http: HttpClient,
    public afAuth: AngularFireAuth,
    private db: AngularFirestore,
    private fns: AngularFireFunctions
  ) {
    this.afAuth.authState.subscribe((user) => {
      this.userData = user ? user : null;
      this.configureAuthState(user);
    });

    this.usersRef = db.collection(this.dbPath);
  }

  private configureAuthState(firebaseUser): void {
    if (firebaseUser) {
      firebase.default
        .auth()
        .currentUser.getIdTokenResult()
        .then((idTokenResult) => {
          // console.log("UserService/getIdTokenResult/:", idTokenResult);

          localStorage.setItem("token", idTokenResult.token);
          this._isLogin.next(true);

          let userFb: User = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          };
          userFb.admin = !!idTokenResult.claims.admin;
          userFb.moderator = !!idTokenResult.claims.moderator;
          userFb.tutor = !!idTokenResult.claims.tutor;
          userFb.student = !!idTokenResult.claims.student;
          userFb.freelancer = !!idTokenResult.claims.freelancer;
          userFb.role = !!idTokenResult.claims.role
            ? idTokenResult.claims.role
            : "freelancer";
          userFb.schoolId = !!idTokenResult.claims.schoolId
            ? idTokenResult.claims.schoolId
            : "";

          console.log("UserService/getIdTokenResult/userFb:", userFb);
          this.currentUserSource.next(userFb);

          //check token in FirebaseApi
          if (environment.production) {
            this.verifyTokenAPI(idTokenResult.token);
          }
        })
        .catch((error) => {
          console.log("UserService/getIdTokenResult/error:", error);
          this.doSignedOutUser();
        });
    } else {
      this.doSignedOutUser();
    }
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    const value = token !== null && token != "" ? true : false;
    //console.log("UserService/isLoggedIn():", value);
    return value;
  }

  uid(): string {
    const uid = this.userData && this.userData.uid ? this.userData.uid : "";
    //console.log("UserService/uid():", uid);
    return uid;
  }

  getCurrentUser(): Observable<User> {
    // console.log("UserService/getCurrentUser/userData:", this.userData);
    // console.log("UserService/getCurrentUser/currentUser$:", this.currentUser$);

    //return this.isLoggedIn() ? this.currentUser$ : new BehaviorSubject(null);

    return this.currentUser$;
  }

  get(id: string) {
    return this.usersRef.doc(id).valueChanges();
  }

  //FireAuth
  // Sign in with email/password
  signIn(email, password) {
    return this.afAuth
      .signInWithEmailAndPassword(email, password)
      .then((result) => {
        //console.log("UserService/signIn:", result.user.uid);
      })
      .catch((error) => {
        //window.alert(error.message);
        throw error;
      });
  }

  signOut() {
    return this.afAuth.signOut().then(() => {
      this.doSignedOutUser();
    });
  }

  resetPassword(email) {
    return this.afAuth.sendPasswordResetEmail(email).catch((error) => {
      throw error;
    });
  }

  private doSignedOutUser() {
    localStorage.removeItem("token");
    this.userData = null;
    this._isLogin.next(false);
    this.currentUserSource.next(null);
  }

  private verifyTokenAPI(theToken) {
    this.http
      .post(this.baseUrl + "/FirebaseAuth/VerifyToken", {
        token: theToken,
      })
      .subscribe({
        next: () => {},
        error: (err) => {
          // this.doSignedOutUser();
        },
      });
  }

  private syncUser(user) {
    this.http.post(this.baseUrl + "/FirebaseAuth", user).subscribe({
      next: () => {},
      error: (err) => {},
    });
  }

  create(uid, email, displayName) {
    const userRef: AngularFirestoreDocument<any> = this.db.doc(`users/${uid}`);
    const userData: UserData = {
      uid: uid,
      email: email,
      displayName: displayName,
      photoURL:
        "https://kasoft-learning.s3.ap-southeast-1.amazonaws.com/Profile/default_avatar.jpg",
      role: "freelancer",
      coins: 0,
      stars: 0,
      hearts: 0,
      noLiked: 0,
      students: 0,
      sharingCourses: 0,
      learningCourses: 0,
      sharingQuizzes: 0,
      learningQuizzes: 0,
      sharingLectures: 0,
      notifications: 0,
      isActive: true,
      isDeleted: false,
    };
    userRef.set(userData, {
      merge: true,
    });

    //Sync user to local
    this.syncUser(userData);
  }

  getToken(): string {
    return localStorage.getItem("token");
  }

  approveData(id: string, mode: string, value: boolean) {
    const callable = this.fns.httpsCallable("moderator-approve");
    return callable({ id: id, mode: mode, value: value });
  }

  //User Data service
  getTopUsers(
    mode?: string,
    limit?: number,
    cursor?: any
  ): AngularFirestoreCollection<UserData> {
    return this.db.collection(this.dbPath, (ref) => {
      let query: CollectionReference | Query = ref;

      query = query
        .where("isDeleted", "==", false)
        .where("isActive", "==", true);

      query =
        mode && mode != ""
          ? query.orderBy(mode, "desc")
          : query.orderBy("updatedOn", "desc");

      query = limit ? query.limit(limit) : query.limit(10);
      if (cursor != null) query = query.startAfter(cursor);

      return query;
    });
  }

  //CRUD User

  getUsers(): AngularFirestoreCollection<UserData> {
    return this.db.collection(this.dbPath, (ref) =>
      ref.where("isDeleted", "==", false).orderBy("updatedOn", "desc")
    );
  }

  createAccount(user: User) {
    const callable = this.fns.httpsCallable("account-create");
    return callable({
      schoolId: this.schoolId,
      email: user.email,
      displayName: user.displayName,
      password: user.password,
      admin: user.admin,
      moderator: user.moderator,
      tutor: user.tutor,
      student: user.student,
      freelancer: user.freelancer,
      role: user.role,
    });
  }

  createUserData(uid, email, displayName) {
    const userRef: AngularFirestoreDocument<any> = this.db.doc(`users/${uid}`);
    const userData: UserData = {
      uid: uid,
      schoolId: this.schoolId,
      email: email,
      displayName: displayName,
      photoURL: "/assets/images/default_avatar.jpg",
      role: "freelancer",
      coins: 0,
      stars: 0,
      hearts: 0,
      noLiked: 0,
      students: 0,
      sharingCourses: 0,
      learningCourses: 0,
      sharingQuizzes: 0,
      learningQuizzes: 0,
      sharingLectures: 0,
      notifications: 0,
      isActive: true,
      isDeleted: false,
      updatedOn: new Date(),
    };
    userRef.set(userData, {
      merge: true,
    });

    //Sync user to local
    this.syncUser(userData);
  }

  updateUserData(data: UserData): Promise<void> {
    return this.usersRef.doc(data.uid).update({
      displayName: data.displayName ? data.displayName : "user",
      role: data.role ? data.role : "",
      updatedOn: new Date(),
    });
  }

  getUserRole(uid) {
    // return this.http.get(this.baseUrl + "/FirebaseAuth/GetRole/" + uid);
    const callable = this.fns.httpsCallable("account-getRoles");
    return callable({
      uid: uid,
    });
  }

  setUserRole(user) {
    //return this.http.post(this.baseUrl + "/FirebaseAuth/SetRole/", user);
    const callable = this.fns.httpsCallable("account-setRoles");
    return callable({
      uid: user.uid,
      schoolId: this.schoolId,
      role: user.role,
      displayName: user.displayName,
      admin: user.admin,
      moderator: user.moderator,
      tutor: user.tutor,
      student: user.student,
      freelancer: user.freelancer,
      disabled: user.disabled,
      password: user.password,
    });
  }
}
